import type { ConditionGroup, RuleConditions } from "@unbox/api";
import { AlertTriangle, ArrowLeft, HelpCircle, Plus } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import {
  useBlocklistRules,
  useCreateBlocklistRule,
  useRulePreview,
  useUpdateBlocklistRule,
} from "@/api/blocklist-rules";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useDebounce } from "@/hooks/use-debounce";
import { cx } from "@/utils/cx";
import { FIELD_OPTIONS, isConditionComplete, makeDefaultCondition } from "./condition-fields";
import { ConditionGroupCard } from "./condition-group";
import { ExemptCreatorList } from "./exempt-creator-list";
import { ImpactPreviewBanner } from "./impact-preview-banner";

const makeGroup = (): ConditionGroup => ({ logic: "all", conditions: [makeDefaultCondition()] });

const isTreeComplete = (tree: RuleConditions) =>
  tree.when.every((g) => g.conditions.every(isConditionComplete)) &&
  tree.except.every((g) => g.conditions.every(isConditionComplete));

const buildConditionsTree = (when: ConditionGroup[], except: ConditionGroup | null): RuleConditions => ({
  version: 2,
  when,
  except: except ? [except] : [],
});

interface RuleBuilderDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleId?: string;
  onBack?: () => void;
  onCreated?: () => void;
}

export function RuleBuilderDrawer({ open, onOpenChange, ruleId, onBack, onCreated }: RuleBuilderDrawerProps) {
  const isEditing = !!ruleId;
  const { data: rulesData } = useBlocklistRules();
  const { data: cohortsData } = useCreatorCohorts();
  const createRule = useCreateBlocklistRule();
  const updateRule = useUpdateBlocklistRule();
  const preview = useRulePreview();

  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [whenGroups, setWhenGroups] = useState<ConditionGroup[]>([makeGroup()]);
  const [exceptGroup, setExceptGroup] = useState<ConditionGroup | null>(null);
  const [hydratedRuleId, setHydratedRuleId] = useState<string | undefined>(undefined);
  const [unblockAlreadyBlocked, setUnblockAlreadyBlocked] = useState(false);
  const [baseline, setBaseline] = useState<{ name: string; conditionsJson: string; enabled: boolean } | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const cohortOptions = useMemo(() => (cohortsData ?? []).map((c) => ({ id: c.id, label: c.name })), [cohortsData]);

  // Hydrate form state from the existing rule once it's available (edit mode only).
  useEffect(() => {
    if (!open) return;
    if (!ruleId) {
      const defaultWhen = [makeGroup()];
      setName("");
      setEnabled(true);
      setWhenGroups(defaultWhen);
      setExceptGroup(null);
      setHydratedRuleId(undefined);
      setUnblockAlreadyBlocked(false);
      setBaseline({
        name: "",
        conditionsJson: JSON.stringify(buildConditionsTree(defaultWhen, null)),
        enabled: true,
      });
      return;
    }
    if (hydratedRuleId === ruleId) return;
    const rule = rulesData?.items.find((r) => r.ruleId === ruleId);
    if (!rule) return;
    const conditions = rule.conditions as unknown as RuleConditions;
    const hydratedWhen = conditions.when.length > 0 ? conditions.when : [makeGroup()];
    const hydratedExcept = conditions.except[0] ?? null;
    setName(rule.name);
    setEnabled(rule.isActive);
    setWhenGroups(hydratedWhen);
    setExceptGroup(hydratedExcept);
    setHydratedRuleId(ruleId);
    setBaseline({
      name: rule.name,
      conditionsJson: JSON.stringify(buildConditionsTree(hydratedWhen, hydratedExcept)),
      enabled: rule.isActive,
    });
  }, [open, ruleId, rulesData, hydratedRuleId]);

  useEffect(() => {
    if (!open) setShowDiscardConfirm(false);
  }, [open]);

  const conditionsTree: RuleConditions = useMemo(
    () => buildConditionsTree(whenGroups, exceptGroup),
    [whenGroups, exceptGroup],
  );
  const debouncedTree = useDebounce(conditionsTree, 400);

  useEffect(() => {
    if (!open) return;
    if (!isTreeComplete(debouncedTree)) return;
    preview.mutate(debouncedTree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedTree]);

  const isDirty =
    !baseline ||
    name !== baseline.name ||
    JSON.stringify(conditionsTree) !== baseline.conditionsJson ||
    enabled !== baseline.enabled ||
    (!enabled && unblockAlreadyBlocked);
  const canSave = name.trim().length > 0 && isTreeComplete(conditionsTree) && isDirty;
  const alreadyBlockedCount = rulesData?.items.find((r) => r.ruleId === ruleId)?.blockedCount ?? 0;
  const effectiveUnblock = alreadyBlockedCount > 0 && unblockAlreadyBlocked;
  const ctaLabel =
    !isEditing || enabled
      ? "Save"
      : baseline?.enabled
        ? "Disable Rule & Save changes"
        : effectiveUnblock
          ? "Unblock creators & Save changes"
          : "Save";

  const handleSave = async () => {
    if (!canSave) return;
    if (isEditing && ruleId) {
      await updateRule.mutateAsync({
        ruleId,
        name: name.trim(),
        conditions: conditionsTree,
        isActive: enabled,
        unblockAlreadyBlocked,
      });
    } else {
      await createRule.mutateAsync({ name: name.trim(), conditions: conditionsTree });
      onCreated?.();
    }
    onOpenChange(false);
  };

  const handleBack = onBack ?? (() => onOpenChange(false));

  const requestClose = () => {
    if (showDiscardConfirm) return; // ✕/overlay/Esc are no-ops while confirming
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleBackRequest = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      handleBack();
    }
  };

  return (
    <SlideoutMenu
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
      isDismissable
    >
      <SlideoutMenu.Header onClose={requestClose}>
        <h2 className="text-lg font-semibold text-primary">{isEditing ? "Edit Rule" : "New Rule"}</h2>
      </SlideoutMenu.Header>

      {showDiscardConfirm ? (
        <>
          <SlideoutMenu.Content className="flex flex-col items-center gap-0 pt-16">
            <div className="flex w-full flex-col items-center gap-4 rounded-2xl border border-secondary bg-unbox-cream-50 px-8 pt-4 pb-8">
              <div className="flex size-16 items-center justify-center rounded-full">
                <AlertTriangle className="size-6 text-warning-500" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-xl font-semibold text-primary">Unsaved changes</p>
                <p className="w-[360px] text-md text-tertiary">
                  Your changes will be permanently lost if you don't save them.
                </p>
              </div>
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-row items-center gap-6">
            <Button
              color="tertiary"
              className="flex-1"
              onClick={() => {
                setShowDiscardConfirm(false);
                handleBack();
              }}
            >
              Discard Changes
            </Button>
            <Button color="secondary" className="flex-1" onClick={() => setShowDiscardConfirm(false)}>
              Keep Editing
            </Button>
            <Button
              color="primary"
              className="w-[184px]"
              isDisabled={!canSave}
              isLoading={createRule.isPending || updateRule.isPending}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </SlideoutMenu.Footer>
        </>
      ) : (
        <>
          <SlideoutMenu.Content className="flex flex-col gap-6">
            <div className="flex flex-row items-end gap-4">
              <div className="flex-1">
                <Input label="Rule Name" placeholder="Name this rule" value={name} onChange={setName} />
              </div>
              <Toggle isSelected={enabled} onChange={setEnabled} label={enabled ? "Rule Enabled" : "Rule Disabled"} />
            </div>

            <div inert={!enabled} className={cx("flex flex-col gap-8", !enabled && "opacity-[0.58]")}>
              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold tracking-wide text-secondary uppercase">
                  When creator matches
                </span>
                {whenGroups.map((group, index) => (
                  <div key={index} className="flex flex-col gap-2">
                    {index > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-secondary" />
                        <span className="text-xs font-semibold text-secondary">AND</span>
                        <div className="h-px flex-1 bg-secondary" />
                      </div>
                    )}
                    <ConditionGroupCard
                      group={group}
                      fieldOptions={FIELD_OPTIONS}
                      cohortOptions={cohortOptions}
                      onChange={(updated) => setWhenGroups(whenGroups.map((g, i) => (i === index ? updated : g)))}
                      onRemoveGroup={
                        whenGroups.length > 1
                          ? () => setWhenGroups(whenGroups.filter((_, i) => i !== index))
                          : undefined
                      }
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-xl border border-secondary bg-unbox-cream-50 p-3 text-sm font-medium text-secondary hover:text-primary hover:border-primary transition-colors"
                  onClick={() => setWhenGroups([...whenGroups, makeGroup()])}
                >
                  <Plus className="size-4 text-quaternary" />
                  Add group logic
                </button>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold tracking-wide text-secondary uppercase">Except when</span>
                  <div className="h-px flex-1 bg-border-secondary" />
                </div>
                {exceptGroup ? (
                  <ConditionGroupCard
                    group={exceptGroup}
                    fieldOptions={FIELD_OPTIONS}
                    cohortOptions={cohortOptions}
                    onChange={setExceptGroup}
                    onRemoveGroup={() => setExceptGroup(null)}
                    dashed
                  />
                ) : (
                  <button
                    type="button"
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-secondary bg-unbox-cream-50 p-3 text-sm font-medium text-secondary hover:text-primary hover:border-primary transition-colors"
                    onClick={() => setExceptGroup(makeGroup())}
                  >
                    <Plus className="size-4 text-quaternary" />
                    Add Exception
                  </button>
                )}

                <div className="flex items-center justify-start gap-2 mt-4">
                  <span className="text-xs font-semibold text-secondary">OR</span>
                  <div className="h-px flex-1 bg-border-secondary" />
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold tracking-wide text-secondary uppercase">
                    They're individually exempt
                  </span>
                  <Tooltip
                    title="These creators are always exempt, even if they match the rule's conditions. They're added here when you unblock someone and choose to keep them exempt."
                    placement="top"
                  >
                    <TooltipTrigger>
                      <HelpCircle className="size-3.5 text-quaternary cursor-help" />
                    </TooltipTrigger>
                  </Tooltip>
                </div>
                <ExemptCreatorList />
              </div>

              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold tracking-wide text-secondary uppercase">Then</span>
                <div>
                  <Badge type="pill-color" color="error" size="sm">
                    Block Creator
                  </Badge>
                </div>
              </div>
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-col gap-3">
            {enabled ? (
              <ImpactPreviewBanner
                matchCount={preview.data?.matchCount ?? 0}
                totalCount={preview.data?.totalCount ?? 0}
                isLoading={preview.isPending}
              />
            ) : (
              <div className="flex flex-col gap-3 rounded-xl border border-warning-200 bg-warning-50 p-4">
                <p className="text-sm text-secondary">
                  From now on, creators won't be blocked by this rule. Anyone it already blocked keeps their status.
                </p>
                <div className="h-px bg-warning-200" />
                <Checkbox
                  size="sm"
                  isSelected={alreadyBlockedCount === 0 ? false : unblockAlreadyBlocked}
                  isDisabled={alreadyBlockedCount === 0}
                  onChange={setUnblockAlreadyBlocked}
                  label={
                    alreadyBlockedCount === 0
                      ? "No creators are currently blocked by this rule."
                      : `Also unblock the ${alreadyBlockedCount} creators already blocked by this rule.`
                  }
                />
              </div>
            )}
            <div className="flex flex-row justify-between gap-3">
              <Button color="secondary" iconLeading={ArrowLeft} onClick={handleBackRequest}>
                Back
              </Button>
              <Button
                color="primary"
                isDisabled={!canSave}
                isLoading={createRule.isPending || updateRule.isPending}
                onClick={handleSave}
              >
                {ctaLabel}
              </Button>
            </div>
          </SlideoutMenu.Footer>
        </>
      )}
    </SlideoutMenu>
  );
}
